<template>
  <div class="mb-5">
    <ExTextInput
      label="Criteria*"
      placeholder="Add criteria"
      v-model="localCriteria"
      required
      dropdownSide="left"
      :dropdownItems="localDropdownItems"
      :class="[
        'criteria-input',
        { 'input-dirty': isInputDirty, 'prefix-dirty': isPrefixDirty }
      ]"
      :customIcon="'help_icon'"
      :showCustomIcon="false"
      @dropdown-input="onChangeItem($event)"
      :selectedItem="localCriteriaPrefix"
    />
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";

const props = defineProps({
  criteria: { type: String, default: "" },
  criteriaPrefix: { type: String, default: "can" },
  dropdownItems: { type: Array, default: () => [] },
  isInputDirty: { type: Boolean, default: false },
  isPrefixDirty: { type: Boolean, default: false },
});

const emits = defineEmits(["update:criteria", "update:criteriaPrefix"]);

const localCriteria = ref(props.criteria);
const localCriteriaPrefix = ref(props.criteriaPrefix);
const localDropdownItems = ref(props.dropdownItems);

watch(localCriteria, (val) => {
  emits("update:criteria", val);
});

watch(
  () => props.criteria,
  (newVal) => {
    localCriteria.value = newVal;
  }
);

watch(
  () => props.criteriaPrefix,
  (newVal) => {
    localCriteriaPrefix.value = newVal;
  }
);

watch(
  () => props.dropdownItems,
  (newVal) => {
    localDropdownItems.value = newVal;
  }
);

const onChangeItem = (event) => {
  localCriteriaPrefix.value = event;
  emits("update:criteriaPrefix", event);
};
</script>

<style>
.criteria-input .left-demonstrated-prepand {
  min-width: 132px;
  top: -17px;
}
.criteria-input .left-proved-prepand {
  min-width: 80px;
  top: -16px;
}
.criteria-input.ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -60px !important;
}
.criteria-input.ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -10px !important;
}
.left-proved-prepand .v-menu__content.select-drop-list {
  margin-top: -4px;
  margin-left: -2px;
}
.code-left-wrap .v-menu__content.select-drop-list {
  padding: 5px 3px;
}

/* Purple/lavender background when only criteria is dirty (text input area only, not prefix dropdown) */
/* For "Can" prefix (default ~65px + 6px gap) */
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 71px, #D9D6FE 71px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Demonstrated" prefix (~132px + 6px gap) */
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 143px, #D9D6FE 143px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Proved" prefix (~80px + 6px gap) */
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 91px, #D9D6FE 91px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* Purple/lavender background when only prefix is dirty (only prefix dropdown, not text area) */
/* For "Can" prefix */
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 79px, white 65px, white 100%) !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-select__selections {
  background: transparent !important;
}

/* For "Demonstrated" prefix */
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 147px, white 137px, white 100%) !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-select__selections {
  background: transparent !important;
}

/* For "Proved" prefix */
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 95px, white 85px, white 100%) !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-select__selections {
  background: transparent !important;
}

/* Purple/lavender background when BOTH prefix AND criteria are dirty (entire input) */
.criteria-input.prefix-dirty.input-dirty .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background-color: #D9D6FE !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty.input-dirty .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty.input-dirty .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty.input-dirty .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty.input-dirty .v-select__selections {
  background: transparent !important;
}

.criteria-input.prefix-dirty .v-text-field .v-input__control .v-input__slot input {
  background-color: transparent !important;
}

.criteria-input.prefix-dirty .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background-color: transparent !important;
}
</style>
